/**
 * Bolt AI API Service
 *
 * Handles all Bolt AI related API calls for the Command Center dashboard.
 * Connects to /api/documents and /api/knowledge endpoints in vh-api.
 */

import api from './api/client';
import { withQuery } from './api/query';
import { logger } from '../lib/logger';

const ENDPOINTS = {
  documents: '/api/documents',
  boltDocuments: '/api/bolt/documents',
  knowledge: '/api/knowledge',
  rag: '/api/rag',
};

// Files above this size route through the signed-URL direct-to-GCS flow —
// Cloud Run rejects HTTP/1 request bodies over 32MiB, so anything near that
// cap can never transit vh-api (UPLOAD-SIGNEDURL-W1). 25MB leaves headroom
// for multipart framing overhead while keeping small uploads on the proven
// multipart path.
const DIRECT_UPLOAD_THRESHOLD_BYTES = 25 * 1024 * 1024;

class BoltAPI {
  /**
   * Get Bolt system status including RAG pipeline health
   * @param {string} jobsiteId - Optional jobsite filter
   * @returns {Promise<Object>} System status
   */
  async getStatus(jobsiteId = null) {
    try {
      // Get document stats which includes processing status
      const response = await api.get(withQuery(`${ENDPOINTS.documents}/stats`, jobsiteId ? { jobsiteId } : {}));
      const stats = response?.data || response;

      // Transform backend stats to status format
      const total = stats.totalDocuments || 0;
      const ready = stats.readyCount || 0;
      const failed = stats.failedCount || 0;

      return {
        status: failed > 0 ? 'degraded' : 'healthy',
        documentsIndexed: ready,
        totalDocuments: total,
        lastSync: stats.lastUpdated || 'N/A',
        vectorDbStatus: 'connected',
        ragPipelineHealth: total > 0 ? Math.round((ready / total) * 100) : 100,
        avgQueryLatency: 'N/A',
        embeddingModel: 'voyage-4-large',
        indexSize: stats.totalSize ? formatFileSize(stats.totalSize) : 'N/A',
        chunkCount: stats.totalChunks || 0,
      };
    } catch (error) {
      logger.error('[BoltAPI] Error fetching status:', { error });
      // Return degraded status on error
      return {
        status: 'offline',
        documentsIndexed: 0,
        totalDocuments: 0,
        lastSync: 'N/A',
        vectorDbStatus: 'error',
        ragPipelineHealth: 0,
        avgQueryLatency: 'N/A',
        embeddingModel: 'voyage-4-large',
        indexSize: 'N/A',
        chunkCount: 0,
      };
    }
  }

  /**
   * Get documents list
   * @param {Object} filters - Filter options
   * @param {string} filters.jobsiteId - Jobsite filter
   * @param {string} filters.category - Category filter
   * @param {string} filters.status - Processing status filter
   * @param {string} filters.search - Search query
   * @returns {Promise<Array>} Array of documents
   */
  async getDocuments(filters = {}) {
    try {
      const response = await api.get(withQuery(ENDPOINTS.documents, filters));
      const documents = response?.data || response?.documents || [];

      // Transform to match expected document format
      return documents.map((doc) => ({
        id: doc._id || doc.id,
        name: doc.title || doc.originalName || doc.fileName || '',
        fileName: doc.fileName || doc.originalName || '',
        category: doc.documentType || doc.category || 'other',
        scope: doc.scope || (doc.jobsiteId ? 'site' : 'company'),
        siteId: doc.jobsiteId?._id || doc.jobsiteId,
        siteName: doc.jobsiteId?.name || null,
        uploadedBy: doc.uploadedBy?.firstName
          ? `${doc.uploadedBy.firstName} ${doc.uploadedBy.lastName || ''}`.trim()
          : doc.uploadedByName || 'Unknown',
        uploadedAt: doc.uploadedAt || doc.createdAt,
        fileSize: doc.fileSize ? formatFileSize(doc.fileSize) : 'N/A',
        rawFileSize: doc.fileSize || 0,
        pageCount: doc.metadata?.pageCount || doc.pageCount || 0,
        wordCount: doc.metadata?.wordCount || 0,
        status: mapProcessingStatus(doc.status || doc.processingStatus),
        indexedAt: doc.processedAt || doc.updatedAt,
        accessCount: doc.accessCount || 0,
        lastAccessed: doc.lastAccessedAt,
        citations: doc.citationCount || 0,
        chunkCount: doc.chunkCount || 0,
        progress: doc.progress ?? null,
        processingError: doc.processingError || null,
        vectorIndexed: doc.vectorIndexed || false,
        // OCR metadata
        ocrPageCount: doc.metadata?.ocrPageCount ?? null,
        ocrTextDensity: doc.metadata?.ocrTextDensity ?? null,
        ocrErrorCount: doc.metadata?.ocrErrorCount ?? null,
        ocrInputTokens: doc.metadata?.ocrInputTokens ?? null,
        ocrOutputTokens: doc.metadata?.ocrOutputTokens ?? null,
        // Entity extraction
        extractionEntities: doc.metadata?.extractionEntities ?? null,
        extractionRelationships: doc.metadata?.extractionRelationships ?? null,
        entitiesStored: doc.metadata?.entitiesStored ?? null,
        // Authority
        authorityLevel: doc.authority?.level ?? null,
        authorityTrades: doc.authority?.trades || [],
        authorityHazardTypes: doc.authority?.hazardTypes || [],
        companyWide: doc.companyWide,
      }));
    } catch (error) {
      logger.error('[BoltAPI] Error fetching documents:', { error });
      throw error;
    }
  }

  /**
   * Upload a document.
   *
   * Files above DIRECT_UPLOAD_THRESHOLD_BYTES are routed through the
   * signed-URL direct-to-GCS flow (init → PUT to GCS → complete): Cloud Run
   * caps HTTP/1 request bodies at 32MiB, so large files can never transit
   * the API itself (UPLOAD-SIGNEDURL-W1 — the prod 36MB upload failure).
   * Smaller files keep the proven multipart path. Both converge on the same
   * backend intake (dedup, pipeline enqueue) and return the same shape.
   *
   * @param {File} file - File to upload
   * @param {Object} metadata - Document metadata
   * @returns {Promise<Object>} Upload result
   */
  async uploadDocument(file, metadata = {}) {
    try {
      if (file.size > DIRECT_UPLOAD_THRESHOLD_BYTES) {
        return await this.uploadDocumentDirect(file, metadata);
      }

      const formData = new FormData();
      formData.append('file', file);

      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.category) formData.append('category', metadata.category);
      if (metadata.documentType) formData.append('documentType', metadata.documentType);
      if (metadata.jobsiteId) formData.append('jobsiteId', metadata.jobsiteId);
      if (metadata.scope) formData.append('scope', metadata.scope);
      if (metadata.description) formData.append('description', metadata.description);

      // @VH_DELETE_CANDIDATE: FormData upload needs refactoring | delete_after=2026-02-15
      const { getAuthToken } = await import('../utils/authToken');
      const token = await getAuthToken();
      const url = `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}${ENDPOINTS.boltDocuments}/upload`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'x-vh-req-id': crypto.randomUUID(),
        },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Upload failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      logger.error('[BoltAPI] Error uploading document:', { error });
      throw error;
    }
  }

  /**
   * Signed-URL direct-to-GCS upload (large files): mint URL → PUT bytes to
   * GCS → register with the API. Same response contract as uploadDocument.
   * @param {File} file
   * @param {Object} metadata
   * @returns {Promise<Object>}
   */
  async uploadDocumentDirect(file, metadata = {}) {
    const { getAuthToken } = await import('../utils/authToken');
    const token = await getAuthToken();
    const baseUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}${ENDPOINTS.boltDocuments}`;
    const authHeaders = {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
      'x-vh-req-id': crypto.randomUUID(),
    };

    // 1. Mint the signed upload URL (validates type/size/jobsite server-side).
    const initRes = await fetch(`${baseUrl}/upload-init`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        category: metadata.category,
        documentType: metadata.documentType,
        jobsiteId: metadata.jobsiteId,
      }),
    });
    if (!initRes.ok) {
      const errorData = await initRes.json().catch(() => ({ message: initRes.statusText }));
      throw new Error(errorData.error || errorData.message || `Upload init failed: ${initRes.status}`);
    }
    const { data: init } = await initRes.json();

    // 2. PUT the bytes straight to GCS — Content-Type must match the minted
    //    signature exactly.
    const putRes = await fetch(init.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`Direct upload to storage failed: ${putRes.status}`);
    }

    // 3. Register the object: dedup-check + pipeline enqueue.
    const completeRes = await fetch(`${baseUrl}/upload-complete`, {
      method: 'POST',
      headers: { ...authHeaders, 'x-vh-req-id': crypto.randomUUID() },
      body: JSON.stringify({
        storageKey: init.storageKey,
        fileName: file.name,
        mimeType: file.type,
        category: metadata.category,
        documentType: metadata.documentType,
        jobsiteId: metadata.jobsiteId,
        title: metadata.title,
        description: metadata.description,
      }),
    });
    if (!completeRes.ok) {
      const errorData = await completeRes.json().catch(() => ({ message: completeRes.statusText }));
      throw new Error(errorData.error || errorData.message || `Upload registration failed: ${completeRes.status}`);
    }
    return await completeRes.json();
  }

  /**
   * Get full document detail including pipelineStats (GET /api/documents/:id).
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} { success, data: { ...document, metadata, pipelineStats } }
   */
  async getDocumentDetail(documentId) {
    try {
      const response = await api.get(`${ENDPOINTS.documents}/${documentId}`);
      return response;
    } catch (error) {
      logger.error('[BoltAPI] Error fetching document detail:', { error });
      throw error;
    }
  }

  /**
   * Get document processing status
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} { success, data: { documentId, status, progress, chunkCount, pageCount, error, updatedAt } }
   */
  async getDocumentStatus(documentId) {
    try {
      const response = await api.get(`${ENDPOINTS.boltDocuments}/${documentId}/status`);
      return response;
    } catch (error) {
      logger.error('[BoltAPI] Error fetching document status:', { error });
      throw error;
    }
  }

  /**
   * Get document pipeline diagnostics
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} { success, data: { document, embeddings, vectorSearch, environment } }
   */
  async getDocumentDiagnose(documentId) {
    try {
      const response = await api.get(`${ENDPOINTS.boltDocuments}/${documentId}/diagnose`);
      return response;
    } catch (error) {
      logger.error('[BoltAPI] Error fetching document diagnostics:', { error });
      throw error;
    }
  }

  /**
   * Delete a document
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteDocument(documentId) {
    try {
      return await api.delete(`${ENDPOINTS.documents}/${documentId}`);
    } catch (error) {
      logger.error('[BoltAPI] Error deleting document:', { error });
      throw error;
    }
  }

  /**
   * Trigger re-indexing of a document
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Re-index result
   */
  async reindexDocument(documentId) {
    try {
      return await api.post(`${ENDPOINTS.boltDocuments}/${documentId}/reindex`);
    } catch (error) {
      logger.error('[BoltAPI] Error re-indexing document:', { error });
      throw error;
    }
  }

  /**
   * Search documents using vector search
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Search results
   */
  async searchDocuments(query, options = {}) {
    try {
      return await api.post(`${ENDPOINTS.documents}/search`, {
        query,
        ...options,
      });
    } catch (error) {
      logger.error('[BoltAPI] Error searching documents:', { error });
      throw error;
    }
  }

  /**
   * Get RAG query for knowledge base
   * @param {string} query - Query text
   * @param {Object} context - Query context
   * @returns {Promise<Object>} RAG response
   */
  async queryKnowledge(query, context = {}) {
    try {
      return await api.post(`${ENDPOINTS.knowledge}/query`, {
        query,
        context,
      });
    } catch (error) {
      logger.error('[BoltAPI] Error querying knowledge:', { error });
      throw error;
    }
  }

  /**
   * Get analytics metrics for Bolt usage
   * Note: These endpoints may need to be implemented in vh-api
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics(filters = {}) {
    const defaults = {
      queryVolume: { today: 0, thisWeek: 0, thisMonth: 0, trend: [] },
      topDocuments: [],
      queryCategories: [],
      workerAdoption: { totalWorkers: 0, activeUsers: 0, adoptionRate: 0 },
      feedback: { totalResponses: 0, helpful: 0, notHelpful: 0, positiveRate: 0, topComplaints: [] },
      responseMetrics: { avgResponseTime: 'N/A', avgConfidence: 0, citationRate: 0, multiTurnRate: 0 },
      peakHours: [],
    };
    try {
      const response = await api.get(withQuery(`${ENDPOINTS.rag}/audit/stats`, filters));
      const stats = response?.data || response;
      if (!stats || typeof stats !== 'object') return defaults;

      // Map backend audit stats to frontend analytics structure
      return {
        queryVolume: {
          today: stats.todayQueries || stats.totalQueries || 0,
          thisWeek: stats.totalQueries || 0,
          thisMonth: stats.totalQueries || 0,
          trend: stats.dailyTrend || [],
        },
        topDocuments: stats.topDocuments || [],
        queryCategories: stats.queryCategories || stats.scopeStats || [],
        workerAdoption: {
          totalWorkers: stats.uniqueUsers || 0,
          activeUsers: stats.uniqueUsers || 0,
          adoptionRate: stats.uniqueUsers > 0 ? 100 : 0,
          topUsers: [],
        },
        feedback: {
          totalResponses: stats.totalQueries || 0,
          helpful: 0,
          notHelpful: 0,
          positiveRate: 0,
          topComplaints: [],
        },
        responseMetrics: {
          avgResponseTime: stats.avgLatency ? `${Math.round(stats.avgLatency)}ms` : 'N/A',
          avgConfidence: stats.avgScore || 0,
          citationRate: 0,
          multiTurnRate: 0,
        },
        peakHours: (stats.hourlyStats || []).map((h) => ({
          hour: h.hour || h._id,
          queries: h.count || h.queries || 0,
        })),
      };
    } catch (error) {
      logger.warn('[BoltAPI] Analytics endpoint not available, using defaults:', { error });
      return defaults;
    }
  }

  /**
   * Get recent BOLT queries for the activity feed
   * @returns {Promise<Array>} Array of recent queries
   */
  async getRecentQueries(filters = {}) {
    try {
      const response = await api.get(withQuery(`${ENDPOINTS.rag}/audit/queries`, filters));
      const data = response?.data || response;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      logger.warn('[BoltAPI] Recent queries endpoint not available:', { error });
      return [];
    }
  }

  /**
   * Get AI suggestions for missing documents
   * @returns {Promise<Array>} Array of suggestions
   */
  async getAISuggestions() {
    try {
      return await api.get(`${ENDPOINTS.knowledge}/suggestions`);
    } catch (error) {
      logger.warn('[BoltAPI] Suggestions endpoint not available:', { error });
      return [];
    }
  }

  /**
   * List BOLT chat conversations (paginated, optional jobsite filter)
   * @param {string|null} jobsiteId
   * @param {number} page
   * @param {number} limit
   * @returns {Promise<{conversations: Array, total: number, page: number, limit: number, pages: number}>}
   */
  async listConversations(jobsiteId = null, page = 1, limit = 20) {
    try {
      const params = { page, limit };
      if (jobsiteId) params.jobsiteId = jobsiteId;
      const body = await api.get(withQuery('/api/bolt/conversations', params));
      const data = body?.data !== undefined ? body.data : body;
      return {
        conversations: Array.isArray(data?.conversations) ? data.conversations : [],
        total: data?.total ?? 0,
        page: data?.page ?? page,
        limit: data?.limit ?? limit,
        pages: data?.pages ?? 0,
      };
    } catch (error) {
      logger.warn('[BoltAPI] Failed to list conversations:', { error });
      throw error;
    }
  }

  /**
   * Load a single conversation with messages
   * @param {string} conversationId
   * @returns {Promise<Object|null>}
   */
  async loadConversation(conversationId) {
    try {
      const body = await api.get(`/api/bolt/conversations/${conversationId}`);
      const data = body?.data !== undefined ? body.data : body;
      return data && typeof data === 'object' ? data : null;
    } catch (error) {
      logger.error('[BoltAPI] Failed to load conversation:', { error });
      return null;
    }
  }

  /**
   * Soft-delete a conversation
   * @param {string} conversationId
   */
  async deleteConversation(conversationId) {
    return api.delete(`/api/bolt/conversations/${conversationId}`);
  }

  /**
   * Update conversation title
   * @param {string} conversationId
   * @param {string} title
   */
  async renameConversation(conversationId, title) {
    return api.patch(`/api/bolt/conversations/${conversationId}/title`, { title });
  }
}

// Helper functions
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function mapProcessingStatus(status) {
  const statusMap = {
    pending: 'pending',
    queued: 'queued',
    processing: 'indexing',
    ready: 'indexed',
    completed: 'indexed',
    failed: 'failed',
  };
  return statusMap[status] || 'pending';
}

// Export singleton instance
const boltAPI = new BoltAPI();
export default boltAPI;

// Export for named imports
export { boltAPI };
